import { uniqBy } from 'lodash';
import { useEffect, useMemo } from 'react';

import type { IVideoCreatorStore } from '../../../stores/IVideoCreatorStore';
import type { BrandingCustomFont } from '../../../types.ts/story';

const LOADED_FONTS = new Set<string>();

const getFontId = (font: BrandingCustomFont) =>
  `${font.fontFamily}-${font.postScriptName}`;

const loadFontFace = async (font: BrandingCustomFont, fontFace: FontFace) => {
  const loadedFont = await fontFace.load();
  LOADED_FONTS.add(getFontId(font));
  document.fonts.add(loadedFont);
};

const useLoadBrandKitFonts = (videoCreator: IVideoCreatorStore) => {
  const fontsToLoad = useMemo(
    () =>
      uniqBy(videoCreator.organization?.branding?.customFonts, f =>
        getFontId(f),
      )
        ?.filter(font => !LOADED_FONTS.has(getFontId(font)))
        .map(font => ({
          font,
          fontFace: new FontFace(font.postScriptName, `url(${font.source})`, {
            style: font.style,
            weight: font.usWeightClass.toString(),
          }),
        })),
    [videoCreator.organization?.branding?.customFonts],
  );

  // Load fonts to the browser
  useEffect(() => {
    if (!fontsToLoad?.length) return;
    fontsToLoad?.forEach(({ font, fontFace }) => {
      loadFontFace(font, fontFace);
    });
  }, [fontsToLoad]);
};

export default useLoadBrandKitFonts;
